<template>
  <div class="do-datepicker">
    <div class="do-datepicker__buttons">
      <DDropdown :isShowDots="false" :propOffset="[0, 5]">
        <button class="bttn-with-icon" slot="title">
          {{ activeShortCutTitle }}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="tw-icon tw-icon-cheveron-down">
            <path class="secondary" fill-rule="evenodd"
                  d="M15.3 10.3a1 1 0 0 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 1.4-1.4l3.3 3.29 3.3-3.3z"/>
          </svg>
        </button>
        <ul slot="list">
          <li v-for="dateShortCut in dateShortCuts"
              @click="setDateFromShortCut(dateShortCut)">
            {{ dateShortCut.title }}
          </li>
        </ul>
      </DDropdown>
      <div class="do-datepicker__dropdown" ref="child_popper"
           v-if="isDateDropdown"
           :class="{'d-dropdown--animate': isPopperReady, 'd-dropdown--show': isPopperDone}">
        <div class="do-datepicker__dropdown__header">
          <label class="d-label">{{ i18n['select_dates'] }}</label>
          <div class="icon__bg-circle" @click="isDateDropdown = false">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="do-datepicker__dropdown__body">
          <Datepickerrange
            :allowSameDate="allowSameDate"
            :maximumDateRange="maximumDateRange"
            :startdate="startDate"
            :enddate="endDate"
            @startchanged="setStartDate"
            @endchanged="setEndDate">
          </Datepickerrange>
        </div>
        <div class="do-datepicker__dropdown__footer">
          <button class="do-bttn do-bttn--submit" @click="submitDateRange">{{ i18n['submit'] }}</button>
        </div>
      </div>
      <button @click="openIsDateDropdown" ref="date_input_button">
        <span class="icon-calendar4"></span>
        {{ dateRangeFormatted }}
      </button>
    </div>
    <div class="bg-overlay do-datepicker__dropdown__overlay" v-if="isDateDropdown"
         @click.stop.self="closeIsDateDropdown"></div>
  </div>
</template>

<script>
import moment from 'moment'
import DDropdown from '../common/DDropdown'
import Datepickerrange from './DatePickerRange'
import {mixin as clickaway} from 'vue-clickaway'
import {createPopper} from '@popperjs/core'

const I18N = {
  da: {
    select_dates: 'Vælg datoer',
    submit: 'Indlæs',
    this_week: 'Denne uge',
    this_month: 'Denne måned',
    school_year: 'Skoleåret',
    custom: 'Tilpasset'
  },
  en: {
    select_dates: 'Select Dates',
    submit: 'Submit',
    this_week: 'This week',
    this_month: 'This month',
    school_year: 'School year',
    custom: 'Custom'
  },
  nb: {
    select_dates: 'Velg datoer',
    submit: 'Submit',
    this_week: 'Denne uke',
    this_month: 'Denne måned',
    school_year: 'Skoleåret',
    custom: 'Tilpasset'
  }
}

export default {
  components: {Datepickerrange, DDropdown},
  mixins: [clickaway],
  props: {
    maximumDateRange: {
      type: Number,
      default: null // Default is null, which means unlimited range
    },
    allowSameDate: {
      type: Boolean,
      default: false
    },
    startDate: {
      default () {
        return moment().startOf('isoWeek')
      }
    },
    endDate: {
      default () {
        return moment().endOf('isoWeek')
      }
    },
    date: {
      default () {
        return moment()
      }
    },
    dateShortCutsProps: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      isDateDropdown: false,
      isPopperReady: false,
      isPopperDone: false,
      initialStartDate: moment(),
      initialEndDate: moment()
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    quartersInRange () {
      let startDate = this.startDate
      const dateRanges = []
      let currentDate = moment(startDate)
      const stopDate = moment(this.endDate)
      while (currentDate <= stopDate) {
        const intervalStart = moment(currentDate).format('YYYY-MM-DD')
        const intervalEnd = moment(currentDate).add(3, 'months').subtract(1, 'days').format('YYYY-MM-DD')
        dateRanges.push({start: intervalStart, end: intervalEnd})
        currentDate = moment(currentDate).add(3, 'months')
      }
      return dateRanges
    },
    schoolYears () {
      return this.$store.state.schoolYears
    },
    activeSchoolYears () {
      return this.schoolYears.filter(schoolYear => schoolYear.active)
    },
    schoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    dateShortCuts () {
      if (this.dateShortCutsProps) {
        return this.dateShortCutsProps
      }
      const custom = [
        {title: this.i18n['this_week'], startDate: moment(this.date).startOf('isoWeek'), endDate: moment(this.date).endOf('isoWeek')},
        {title: this.i18n['this_month'], startDate: moment(this.date).startOf('month'), endDate: moment(this.date).endOf('month')}
      ]
      const schoolYears = this.activeSchoolYears.map(schoolYear => {
        return {
          title: this.i18n['school_year'] + ' ' + schoolYear.name,
          startDate: moment(schoolYear.employee_time_start).startOf('day'),
          endDate: moment(schoolYear.employee_time_end).endOf('day')
        }
      })
      return custom.concat(schoolYears)
    },
    activeShortCutTitle () {
      const idx = this.dateShortCuts.findIndex(shortCut => moment(shortCut.startDate).isSame(this.startDate, 'day') && moment(shortCut.endDate).isSame(this.endDate, 'day'))
      return idx < 0 ? this.i18n['custom'] : this.dateShortCuts[idx].title
    },
    dateRangeFormatted () {
      let startFormat = this.startDate.format('D.')
      if (!moment(this.startDate).isSame(this.endDate, 'month')) {
        startFormat = this.startDate.format('D. MMM')
      }
      if (!moment(this.startDate).isSame(this.endDate, 'year')) {
        startFormat = this.startDate.format('D. MMM, YYYY')
      }
      const endFormat = this.endDate.format('D. MMM, YYYY')
      return `${startFormat} - ${endFormat}`
    }
  },
  methods: {
    setDropdown () {
      this.isDropDown = !this.isDropDown
      if (this.isDropDown) {
        setTimeout(_ => {
          this.createPopper()
        }, 0)
      } else {
        this.isPopperReady = false
        this.isPopperDone = false
      }
    },
    openIsDateDropdown () {
      this.isDateDropdown = true
      this.$nextTick(_ => {
        this.createPopper()
      })
    },
    closeIsDateDropdown () {
      this.isDropdownPopperDone = false
      this.isDateDropdown = false
    },
    createPopper () {
      this.isPopperReady = false
      this.isPopperDone = false
      let dChild = this.$refs.child_popper
      let dParent = this.$refs.date_input_button
      if (this.appendToBody) {
        document.getElementById('djs-body-click').appendChild(dChild)
      }
      createPopper(dParent, dChild, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1]
            }
          }
        ]
      })
      setTimeout(_ => {
        this.isPopperReady = true
        this.isPopperDone = true
      }, 0)
    },
    setDateFromShortCut (dateShortCut) {
      const start = dateShortCut.startDate
      const end = dateShortCut.endDate
      this.$emit('startChanged', start)
      this.$emit('endChanged', end)
      this.$emit('datesChanged', {start, end})
    },
    setStartDate (date) {
      this.initialStartDate = date
    },
    setEndDate (date) {
      this.initialEndDate = date
    },
    submitDateRange () {
      this.$emit('startChanged', this.initialStartDate)
      this.$emit('endChanged', this.initialEndDate)
      const start = this.initialStartDate
      const end = this.initialEndDate
      this.$emit('datesChanged', {start, end})
      this.isDateDropdown = false
    },
    closeDoDatepickerDropdown () {
      this.isDateDropdown = false
    }
  }
}
</script>

<style scoped>
.do-datepicker__buttons {
  display: inline-flex;
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, .12) 0px 1px 1px 0px, rgba(64, 68, 82, .16) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(64, 68, 82, .08) 0px 2px 5px 0px;
  border: 0;
  background: #fff;
  font-weight: 600;
  letter-spacing: .025em;
  border-radius: 4px;
}

.do-datepicker__buttons > button {
  background: transparent;
  border: 0;
}

.do-datepicker__buttons > button:first-child {
  border-radius: 4px 0 0 4px;
}

.do-datepicker__buttons > button:last-child {
  border-radius: 0 4px 4px 0;
  border-left: 1px solid #d9dadd;
}

.bttn-with-icon {
  display: flex;
  align-items: center;
  background: transparent;
  border: 0;
  font-size: 12px;
}

.bttn-with-icon .tw-icon {
  width: 22px;
}

.do-datepicker {
  position: relative;
}

.do-datepicker__dropdown {
  z-index: 123103;
  background: #ffffff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, .24);
  border-radius: 4px;
  width: 500px;
  min-width: 500px;
  opacity: 0;
  margin-top: -10px !important;
}

.do-datepicker__dropdown.d-dropdown--animate {
  transition: opacity 0.25s, margin-top 0.25s;
}

.do-datepicker__dropdown.d-dropdown--show {
  margin-top: 0 !important;
  opacity: 1;
}

.do-datepicker__dropdown__overlay {
  z-index: 123102;
  background: transparent;
}

.do-datepicker__dropdown__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f6f9fc;
  border-bottom: 1px solid #cbd6e0;
  padding: 0.5rem 1rem;
  border-radius: 4px 4px 0 0;
}

.do-datepicker__dropdown__body {
  padding: 0.75rem 1rem;
}

.do-datepicker__dropdown__footer {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 1rem;
  background: #f6f9fc;
  border-top: 1px solid #cbd6e0;
}
</style>



// WEBPACK FOOTER //
// DoDatePickerRange.vue?1a4dc368